import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Blend, Maximize2, ChevronDown, Eye, EyeOff, ImagePlus, Lock, Trash2, Unlock } from "lucide-react";
import { Button } from "../../ui/button";
import { PlacementNumberField } from "./PlacementNumberField";
import { MetadataElementLayout } from "./MetadataElementLayout";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { useDevOverlayStore } from "../../../stores/dev-overlay-store";

export function DevOverlayControls() {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasOverlay = useDevOverlayStore(state => !!state.overlay);
  const opacity = useDevOverlayStore(state => state.opacity);
  const locked = useDevOverlayStore(state => state.locked);
  const hidden = useDevOverlayStore(state => state.hidden);
  const transform = useDevOverlayStore(state => state.overlay?.transform);
  const LockIcon = locked ? Lock : Unlock;
  const HideIcon = hidden ? EyeOff : Eye;
  const transformFields = [
    { key: "x", label: t("devOverlay.x"), value: transform?.x ?? 0 },
    { key: "y", label: t("devOverlay.y"), value: transform?.y ?? 0 },
    { key: "size", label: t("devOverlay.size"), value: (transform?.scaleX ?? 1) * 100 },
    { key: "opacity", label: t("devOverlay.opacity"), value: opacity },
  ] as const;

  const controls = (
    <div className="space-y-2">
      <div role="toolbar" aria-label={t("devOverlay.toolbar")} className="space-y-2">
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-200/60 bg-neutral-50/50 pt-2">
          <Button variant="action" size="sm" disabled={loading} onClick={() => input.current?.click()}>
            <ImagePlus />{t("devOverlay.add")}
          </Button>
          <Button variant="action" size="sm" disabled={!hasOverlay} onClick={() => useDevOverlayStore.getState().remove()}>
            <Trash2 />{t("devOverlay.remove")}
          </Button>
          <Button variant="action" size="sm" disabled={!hasOverlay} onClick={() => useDevOverlayStore.getState().setHidden(!hidden)}>
            <HideIcon />{t(hidden ? "devOverlay.show" : "devOverlay.hide")}
          </Button>
          <Button variant="action" size="sm" disabled={!hasOverlay} onClick={() => useDevOverlayStore.getState().setLocked(!locked)}>
            <LockIcon />{t(locked ? "devOverlay.unlock" : "devOverlay.lock")}
          </Button>
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async event => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setLoading(true);
            setFailed(false);
            const url = URL.createObjectURL(file);
            try {
              const image = new Image();
              image.src = url;
              await image.decode();
              useDevOverlayStore.getState().setImage(image);
            } catch {
              setFailed(true);
            } finally {
              URL.revokeObjectURL(url);
              setLoading(false);
            }
          }}
        />
      </div>
      <MetadataElementLayout fields={transformFields.map(({ key, label, value }) => ({
        key,
        content: <PlacementNumberField id={`${id}-${key}`} value={Math.round(value * 100) / 100}
          label={key === "size" ? <Maximize2 className="size-3.5" /> : key === "opacity" ? <Blend className="size-3.5" /> : key.toUpperCase()}
          title={label} size={key === "size"} opacity={key === "opacity"} disabled={!hasOverlay}
          onChange={next => {
            if (key === "opacity") {
              useDevOverlayStore.getState().setOpacity(next);
              return;
            }
            const current = useDevOverlayStore.getState().overlay?.transform;
            if (!current) return;
            useDevOverlayStore.getState().setTransform(key === "size"
              ? { ...current, scaleX: next / 100, scaleY: next / 100 }
              : { ...current, [key]: next });
          }} />,
      }))} />
      {failed && <p role="alert" className="text-xs text-red-600">{t("devOverlay.uploadError")}</p>}
    </div>
  );
  return (
    <Collapsible defaultOpen={false} className="shrink-0 border-t border-neutral-200 pt-4">
      <h3>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="group w-full justify-between">
            {t("devOverlay.title")}
            <ChevronDown className="transition-transform group-data-[state=closed]:-rotate-90" />
          </Button>
        </CollapsibleTrigger>
      </h3>
      <CollapsibleContent role="region" aria-label={t("devOverlay.title")} className="pt-3 data-[state=closed]:hidden">
        {controls}
      </CollapsibleContent>
    </Collapsible>
  );
}
